import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
    IconMore,
    Paragraph,
    Image,
    Menu,
    MenuItem,
    MenuPopper,
    Anchor,
    Tooltip,
    IconHelp,
    Spacer,
    Flex,
    Button,
} from '@zillow/constellation';

import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';
import { GA_EVENTS, GA_PREFIX } from 'app/shared/constants/AnalyticsLeasesModuleConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';
import ConnectedStatus from 'app/shared/components/ConnectedStatus';
import { StyledEllipsisParagraph } from 'app/shared/StyledComponents/StyledComponents';
import leasePreviewPlaceholder from 'app/shared/images/placeholder-lease-thumbnail.svg';
import WhoHasSignedDescription from '../WhoHasSignedDescription';

import {
    StyledFlex,
    StyledSpacer,
    StyledFullCard,
    StyledDesktopOnlyDiv,
    StyledIconButton,
    StyledTooltipPopper,
    StyledTriggerText,
} from './StyledComponents';

const {
    PRIMARY_CTA_CLICK,
    SECONDARY_CTA_CLICK,
    SECONDARY_CTA_ITEM_CLICK,
} = GA_EVENTS;

const {
    ACCESS_INDIVIDUAL_LEASE,
    OPEN_SECONDARY_MENU,
    SECONDARY_MENU_SELECTION,
} = GA_PREFIX;

const LeaseCard = ({ lease, isMultipleLeases, index }) => {
    const dispatch = useDispatch();

    const {
        leaseStatusText,
        leaseStatusType,
        leaseThumbnailUrl,
        leaseName,
        leaseDescription,
        ctaButtonText,
        ctaButtonUrl,
        overflowButtons,
        signeeList,
        descriptionTooltip,
    } = lease;

    // status values are return in all CAPPS
    // make them lowercase before formatting
    const GA_SUFFIX = `${DELIMITER}${stringHelper.startCaseRemoveSpace(leaseStatusText.toLowerCase())}`;

    const handlePrimaryCTAClick = () => {
        dispatch(analyticsUtils.track.GAEventV2({
            ...PRIMARY_CTA_CLICK,
            action: `${ACCESS_INDIVIDUAL_LEASE}${GA_SUFFIX}`,
        }));
    };

    const handleOverflowMenuOpen = () => {
        dispatch(analyticsUtils.track.GAEventV2({
            ...SECONDARY_CTA_CLICK,
            action: `${OPEN_SECONDARY_MENU}${GA_SUFFIX}`,
        }));
    };

    const handleOverflowMenuItemClick = (text) => {
        dispatch(analyticsUtils.track.GAEventV2({
            ...SECONDARY_CTA_ITEM_CLICK,
            action: `${SECONDARY_MENU_SELECTION}${DELIMITER}${stringHelper.startCaseRemoveSpace(text)}${GA_SUFFIX}`,
        }));
    };

    const renderMenu = (menuItems) => {
        if (menuItems && menuItems.length) {
            return (
                <Menu>
                    {menuItems.map(({ text, url }, i) => (
                        <Anchor
                            key={`anchor-${i}`}
                            href={url}
                            onClick={() => handleOverflowMenuItemClick(text)}
                        >
                            <MenuItem>{text}</MenuItem>
                        </Anchor>
                    ))}
                </Menu>
            );
        }

        return null;
    };

    const renderTooltip = (tooltipConfig) => {
        if (!tooltipConfig) {
            return null;
        }

        const tooltip = (
            <Tooltip body={(
                <>
                    <Paragraph paddingBottom="xs" fontType="bodyHeading">{tooltipConfig.title}</Paragraph>
                    <Paragraph fontType="bodySmall">
                        {tooltipConfig.text}
                    </Paragraph>
                </>
            )}
            />
        );

        return (
            <StyledTooltipPopper triggered={tooltip}>
                <StyledTriggerText><IconHelp fontColor="brand" /></StyledTriggerText>
            </StyledTooltipPopper>
        );
    };

    const renderDescription = (description, whoHasSignedList = []) => {
        if (description && description.type && description.text) {
            switch (description.type) {
                case 'active':
                    return (
                        <WhoHasSignedDescription text={description.text} signeeList={whoHasSignedList} />
                    );
                case 'caution':
                    return (
                        <Paragraph
                            data-test="connected-caution-description"
                            paddingTop={0.5}
                            fontType="bodySmall"
                            marginBottom="sm"
                            mediaQuery={{
                                xl: {
                                    marginBottom: isMultipleLeases ? 'sm' : 'md',
                                },
                            }}
                            fontColor="red400"
                        >
                            {description.text}
                        </Paragraph>
                    );
                default:
                    return (
                        <Paragraph
                            data-test="connected-neutral-description"
                            paddingTop={0.5}
                            fontType="bodySmall"
                            marginBottom="sm"
                            mediaQuery={{
                                xl: {
                                    marginBottom: isMultipleLeases ? 'sm' : 'md',
                                },
                            }}
                        >
                            {description.text}
                            {' '}
                            {renderTooltip(descriptionTooltip)}
                        </Paragraph>
                    );
            }
        }

        return null;
    };

    return (
        <StyledFullCard
            isDisabled={true}
            isMultipleLeases={isMultipleLeases}
            className="LeaseCard"
        >
            <Spacer marginRight="xs" className="ConnectedLeaseCard-LeaseInformation">
                <ConnectedStatus message={leaseStatusText} type={leaseStatusType} size="sm" />
                <StyledEllipsisParagraph paddingTop="sm" fontType="bodyHeading">
                    {leaseName || `Lease #${index + 1}`}
                </StyledEllipsisParagraph>
                {renderDescription(leaseDescription, signeeList)}
            </Spacer>
            <StyledDesktopOnlyDiv
                className="ConnectedLeaseCard-LeaseThumbnail"
                mediaQuery={{
                    lg: {
                        marginBottom: 'md',
                    },
                }}
                isMultipleLeases={isMultipleLeases}
            >
                {leaseThumbnailUrl ? (
                    <Image
                        className="LeasePreview"
                        data-test="lease-preview"
                        src={leaseThumbnailUrl}
                        alt="preview image of lease"
                    />
                ) : (
                    <Image
                        className="LeasePreviewPlaceholder"
                        data-test="lease-preview-placeholder"
                        src={leasePreviewPlaceholder}
                        alt="lease preview placeholder"
                    />
                )}
            </StyledDesktopOnlyDiv>
            <StyledFlex
                display="flex"
                className="ConnectedLeaseCard-CTAs"
                aria-label="leasing action buttons"
            >
                <Flex flexGrow={1}>
                    <StyledSpacer>
                        <Button
                            as="a"
                            size="sm"
                            buttonType="primary"
                            fluid={true}
                            href={ctaButtonUrl}
                            onClick={handlePrimaryCTAClick}
                        >
                            {ctaButtonText}
                        </Button>
                    </StyledSpacer>
                </Flex>
                <Flex flexGrow={1}>
                    <MenuPopper triggered={renderMenu(overflowButtons)} onOpen={handleOverflowMenuOpen}>
                        <StyledIconButton fluid={true} size="sm" buttonType="secondary">
                            <IconMore size="sm" />
                        </StyledIconButton>
                    </MenuPopper>
                </Flex>
            </StyledFlex>
        </StyledFullCard>
    );
};

LeaseCard.propTypes = {
    lease: PropTypes.shape({
        leaseId: PropTypes.number,
        leaseStatusText: PropTypes.string.isRequired,
        leaseStatusType: PropTypes.string.isRequired,
        leaseThumbnailUrl: PropTypes.string,
        leaseName: PropTypes.string,
        leaseDescription: PropTypes.shape({
            type: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
        }).isRequired,
        descriptionTooltip: PropTypes.shape({
            title: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
        }),
        ctaButtonText: PropTypes.string.isRequired,
        ctaButtonUrl: PropTypes.string.isRequired,
        overflowButtons: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        })),
        signeeList: PropTypes.arrayOf(PropTypes.shape({
            type: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
        })),
    }).isRequired,
    isMultipleLeases: PropTypes.bool,
    index: PropTypes.number,
};

LeaseCard.defaultProps = {
    isMultipleLeases: false,
    index: '',
};

export default LeaseCard;
